import { useState, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Upload, CheckCircle2, Loader2, Phone, Mail, MapPin, ArrowRight } from "lucide-react";
import { Reveal, SectionLabel } from "./Reveal";
import { SERVICE_OPTIONS, CONTACT } from "@/lib/data";
import { useI18n } from "@/context/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full h-12 px-4 bg-white border-2 border-charcoal/20 font-body text-charcoal placeholder:text-charcoal/30 focus:border-safety focus:outline-none focus:ring-0 transition-colors";

export const QuoteForm = () => {
  const { t } = useI18n();
  const q = t.quote;
  const [form, setForm] = useState({
    full_name: "", company_name: "", phone: "", location: "",
    service_type: SERVICE_OPTIONS[0], estimate: "", message: "",
  });
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const fileRef = useRef();

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const FieldLabel = ({ children }) => (
    <label className="block font-mono2 text-[11px] font-700 uppercase tracking-wider text-charcoal/70 mb-2">{children}</label>
  );

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name.trim() || !form.phone.trim()) {
      toast.error(q.errRequired);
      return;
    }
    setLoading(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      if (file) fd.append("drawing", file);
      await axios.post(`${API}/rfq`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setDone(true);
      toast.success(q.okToast);
    } catch (err) {
      toast.error(q.errToast);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="quote" className="bg-charcoal text-white py-16 sm:py-20 lg:py-32 relative noise overflow-hidden">
      <div className="absolute top-0 left-0 right-0 h-2 stripes" />
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <Reveal>
            <SectionLabel index="/ 04">{t.labels.quote}</SectionLabel>
            <h2 className="font-display font-700 uppercase text-4xl sm:text-5xl lg:text-6xl leading-[0.95] mb-6">
              {q.heading[0]} <span className="text-safety">{q.heading[1]}</span>
            </h2>
            <p className="text-white/60 text-base leading-relaxed mb-10 max-w-md">{q.sub}</p>

            <div className="space-y-5">
              {[
                { icon: Phone, label: q.contact.call, value: `${CONTACT.phone}  ·  ${CONTACT.phone2}`, href: `tel:${CONTACT.phoneRaw}` },
                { icon: Mail, label: q.contact.email, value: CONTACT.email, href: `mailto:${CONTACT.email}` },
                { icon: MapPin, label: `${q.contact.workshop} · ${CONTACT.contactPerson}`, value: CONTACT.address },
              ].map((c, i) => (
                <div key={i} className="flex items-start gap-4">
                  <div className="w-11 h-11 border-2 border-safety text-safety flex items-center justify-center flex-shrink-0">
                    <c.icon size={18} />
                  </div>
                  <div>
                    <div className="font-mono2 text-[10px] uppercase tracking-widest text-white/40">{c.label}</div>
                    {c.href ? (
                      <a href={c.href} className="text-white hover:text-safety transition-colors font-500">{c.value}</a>
                    ) : (
                      <div className="text-white font-500 max-w-xs">{c.value}</div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </Reveal>
        </div>

        <div className="lg:col-span-7">
          <Reveal delay={0.15}>
            {done ? (
              <div data-testid="rfq-success" className="bg-white text-charcoal border-2 border-safety p-10 lg:p-14 text-center hard-shadow-accent">
                <CheckCircle2 size={56} className="text-safety mx-auto mb-6" />
                <h3 className="font-display font-700 uppercase text-3xl mb-3">{q.successTitle}</h3>
                <p className="text-charcoal/60 max-w-sm mx-auto mb-8">{q.successBody(form.full_name.split(" ")[0], form.phone)}</p>
                <button
                  onClick={() => { setDone(false); setForm({ full_name: "", company_name: "", phone: "", location: "", service_type: SERVICE_OPTIONS[0], estimate: "", message: "" }); setFile(null); }}
                  className="font-mono2 text-xs font-700 uppercase tracking-wider px-6 h-11 border-2 border-charcoal hover:bg-charcoal hover:text-white transition-colors"
                >
                  {q.another}
                </button>
              </div>
            ) : (
              <form onSubmit={submit} data-testid="rfq-form" className="bg-white text-charcoal border-2 border-white/0 p-6 sm:p-8 lg:p-10">
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <FieldLabel>{q.fields.name}</FieldLabel>
                    <input data-testid="rfq-name" className={inputCls} value={form.full_name} onChange={update("full_name")} placeholder={q.ph.name} required />
                  </div>
                  <div>
                    <FieldLabel>{q.fields.company}</FieldLabel>
                    <input data-testid="rfq-company" className={inputCls} value={form.company_name} onChange={update("company_name")} placeholder={q.ph.company} />
                  </div>
                  <div>
                    <FieldLabel>{q.fields.phone}</FieldLabel>
                    <div className="flex">
                      <span className="h-12 px-3 flex items-center bg-charcoal text-white font-mono2 text-sm border-2 border-charcoal">+91</span>
                      <input data-testid="rfq-phone" className={`${inputCls} border-l-0`} value={form.phone} onChange={update("phone")} placeholder={q.ph.phone} required />
                    </div>
                  </div>
                  <div>
                    <FieldLabel>{q.fields.location}</FieldLabel>
                    <input data-testid="rfq-location" className={inputCls} value={form.location} onChange={update("location")} placeholder={q.ph.location} />
                  </div>
                  <div>
                    <FieldLabel>{q.fields.service}</FieldLabel>
                    <select data-testid="rfq-service" className={`${inputCls} appearance-none cursor-pointer`} value={form.service_type} onChange={update("service_type")}>
                      {SERVICE_OPTIONS.map((o, i) => <option key={o} value={o}>{q.services[i]}</option>)}
                    </select>
                  </div>
                  <div>
                    <FieldLabel>{q.fields.estimate}</FieldLabel>
                    <input data-testid="rfq-estimate" className={inputCls} value={form.estimate} onChange={update("estimate")} placeholder={q.ph.estimate} />
                  </div>
                </div>

                <div className="mt-5">
                  <FieldLabel>{q.fields.drawing}</FieldLabel>
                  <input ref={fileRef} type="file" data-testid="rfq-file" className="hidden"
                    accept=".pdf,.dwg,.dxf,.step,.stp,.zip,.png,.jpg,.jpeg"
                    onChange={(e) => setFile(e.target.files[0] || null)} />
                  <button type="button" onClick={() => fileRef.current?.click()}
                    className="w-full h-14 border-2 border-dashed border-charcoal/30 flex items-center justify-center gap-3 text-charcoal/60 hover:border-safety hover:text-charcoal transition-colors font-mono2 text-sm">
                    <Upload size={18} />
                    {file ? file.name : q.ph.upload}
                  </button>
                </div>

                <div className="mt-5">
                  <FieldLabel>{q.fields.message}</FieldLabel>
                  <textarea data-testid="rfq-message" rows={4} className={`${inputCls} h-auto py-3 resize-none`} value={form.message} onChange={update("message")} placeholder={q.ph.message} />
                </div>

                <button type="submit" disabled={loading} data-testid="rfq-submit"
                  className="group mt-7 w-full h-14 bg-safety text-white font-mono2 text-sm font-700 uppercase tracking-wider flex items-center justify-center gap-3 hover:translate-x-[3px] hover:translate-y-[3px] transition-transform hard-shadow-sm disabled:opacity-70 disabled:translate-x-0 disabled:translate-y-0">
                  {loading ? <><Loader2 size={18} className="animate-spin" /> {q.submitting}</>
                    : <>{q.submit} <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" /></>}
                </button>
              </form>
            )}
          </Reveal>
        </div>
      </div>
    </section>
  );
};
